/**
 * The keyboard shortcut reference, opened with `?` (docs/06-UI-SPEC.md §16).
 *
 * A card over the board rather than a page of its own, so a teacher who has forgotten
 * a key can look it up mid-lesson and carry straight on. Esc closes it, and so does a
 * click anywhere outside it.
 *
 * The list itself is `shortcuts.ts`, beside the code that reads the keys, so the sheet
 * can never drift from what the keys actually do.
 */

import { SHORTCUT_GROUPS, type ShortcutGroup } from '../../board/shortcuts.js';

export interface ShortcutSheetProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

/** One group of shortcuts: a heading and its rows. */
function Group({ group }: { readonly group: ShortcutGroup }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-medium text-text-secondary">{group.title}</h3>
      <dl className="flex flex-col gap-1">
        {group.rows.map((row) => (
          <div key={row.keys} className="flex items-baseline justify-between gap-4">
            <dt className="shrink-0">
              <kbd className="tabular rounded-sm border border-border bg-surface-sunken px-2 py-1 text-xs text-text-primary">
                {row.keys}
              </kbd>
            </dt>
            <dd className="text-right text-sm text-text-primary">{row.action}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function ShortcutSheet({ open, onClose }: ShortcutSheetProps) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="shortcut-sheet-title"
        className="flex max-h-full w-full max-w-[640px] flex-col gap-6 overflow-y-auto rounded-lg bg-surface p-8 shadow-xl"
      >
        <div className="flex items-start justify-between gap-4">
          <h2 id="shortcut-sheet-title" className="text-xl font-semibold text-text-primary">
            Keyboard shortcuts
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="h-touch rounded-md border border-border bg-surface px-4 text-base font-medium text-text-primary transition duration-fast ease-out hover:bg-surface-hover"
          >
            Close
          </button>
        </div>
        <div className="grid gap-6 sm:grid-cols-2">
          {SHORTCUT_GROUPS.map((group) => (
            <Group key={group.title} group={group} />
          ))}
        </div>
      </div>
    </div>
  );
}
